/**
 * 토너먼트 대진표 생성 및 관리 유틸리티
 * 실제 배포 환경에 최적화된 매칭 시스템
 */

// 2의 거듭제곱으로 올림 (대진표에 필요한 최소 슬롯 수)
export function getNextPowerOfTwo(n) {
  if (n <= 0) return 1;
  return Math.pow(2, Math.ceil(Math.log2(n)));
}

// 안전한 랜덤 셔플 (Fisher-Yates 알고리즘)
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// 고유 ID 생성 (배포 환경에서 충돌 방지)
function generateMatchId(type, round, match, timestamp = Date.now()) {
  return `${type}-r${round}-m${match}-${timestamp}-${Math.random().toString(36).substr(2, 9)}`;
}

// 토너먼트 대진표 생성 (단일 엘리미네이션)
export function generateTournamentBracket(participants) {
  if (!participants || !Array.isArray(participants) || participants.length === 0) {
    return { 
      rounds: [], 
      totalRounds: 0, 
      totalParticipants: 0,
      totalSlots: 0,
      type: 'tournament',
      createdAt: new Date().toISOString(),
      status: 'empty'
    };
  }

  // 참가자 수 검증
  if (participants.length < 2) {
    throw new Error('토너먼트에는 최소 2명의 참가자가 필요합니다.');
  }

  const totalSlots = getNextPowerOfTwo(participants.length);
  const totalRounds = Math.log2(totalSlots);
  const shuffledParticipants = shuffleArray(participants);

  // 첫 번째 라운드 생성
  const firstRound = [];
  for (let i = 0; i < totalSlots; i += 2) {
    const player1 = shuffledParticipants[i] || null;
    const player2 = shuffledParticipants[i + 1] || null;
    
    // 상태 결정 로직 개선
    let status, winner = null;
    if (player1 && player2) {
      status = 'pending';
    } else if (player1 || player2) {
      status = 'bye';
      winner = player1 ? player1.userId : player2.userId;
    } else {
      status = 'waiting';
    }

    firstRound.push({
      id: generateMatchId('tournament', 1, Math.floor(i / 2) + 1),
      roundNumber: 1,
      matchNumber: Math.floor(i / 2) + 1,
      player1,
      player2,
      winner,
      status,
      scores: { player1: null, player2: null },
      createdAt: new Date().toISOString(),
      updatedAt: null
    });
  }

  // 후속 라운드 생성
  const rounds = [firstRound];
  for (let round = 2; round <= totalRounds; round++) {
    const previousRound = rounds[round - 2];
    const currentRound = [];
    
    for (let i = 0; i < previousRound.length; i += 2) {
      currentRound.push({
        id: generateMatchId('tournament', round, Math.floor(i / 2) + 1),
        roundNumber: round,
        matchNumber: Math.floor(i / 2) + 1,
        player1: null,
        player2: null,
        winner: null,
        status: 'waiting',
        scores: { player1: null, player2: null },
        previousMatches: [
          previousRound[i]?.id,
          previousRound[i + 1]?.id
        ].filter(Boolean),
        createdAt: new Date().toISOString(),
        updatedAt: null
      });
    }
    rounds.push(currentRound);
  }

  return {
    rounds,
    totalRounds,
    totalParticipants: participants.length,
    totalSlots,
    type: 'tournament',
    createdAt: new Date().toISOString(),
    status: 'active'
  };
}

// 리그전 대진표 생성 (모든 팀이 서로 한 번씩)
export function generateLeagueBracket(participants) {
  if (!participants || !Array.isArray(participants) || participants.length < 2) {
    return { 
      matches: [], 
      totalMatches: 0, 
      type: 'league',
      createdAt: new Date().toISOString(),
      status: 'empty'
    };
  }

  const matches = [];
  let matchId = 1;
  const timestamp = Date.now();
  
  // 모든 참가자 조합 생성
  for (let i = 0; i < participants.length; i++) {
    for (let j = i + 1; j < participants.length; j++) {
      matches.push({
        id: generateMatchId('league', 1, matchId, timestamp),
        matchNumber: matchId,
        player1: participants[i],
        player2: participants[j],
        winner: null,
        status: 'pending',
        scores: { player1: null, player2: null },
        round: Math.ceil(matchId / Math.floor(participants.length / 2)),
        createdAt: new Date().toISOString(),
        updatedAt: null
      });
      matchId++;
    }
  }

  return {
    matches,
    totalMatches: matches.length,
    totalParticipants: participants.length,
    type: 'league',
    createdAt: new Date().toISOString(),
    status: 'active'
  };
}

// 스위스 시스템 대진 생성 (배포 환경 최적화)
export function generateSwissBracket(participants, roundNumber = 1, maxRounds = null) {
  if (!participants || !Array.isArray(participants) || participants.length < 2) {
    return { 
      matches: [], 
      totalMatches: 0, 
      roundNumber, 
      type: 'swiss',
      createdAt: new Date().toISOString(),
      status: 'empty'
    };
  }

  // 최대 라운드 수 계산 (로그 기반)
  const calculatedMaxRounds = maxRounds || Math.ceil(Math.log2(participants.length));
  
  let sortedParticipants;
  if (roundNumber === 1) {
    // 첫 라운드: 랜덤 셔플
    sortedParticipants = shuffleArray(participants);
  } else {
    // 이후 라운드: 점수순 정렬
    sortedParticipants = [...participants].sort((a, b) => {
      const pointsA = a.swissPoints || 0;
      const pointsB = b.swissPoints || 0;
      if (pointsA !== pointsB) return pointsB - pointsA;
      // 동점시 승부차기록 고려
      return (b.tiebreaker || 0) - (a.tiebreaker || 0);
    });
  }

  const matches = [];
  const usedPlayers = new Set();
  let matchId = 1;
  const timestamp = Date.now();

  // 매칭 알고리즘 (이전 상대 중복 방지)
  for (let i = 0; i < sortedParticipants.length; i++) {
    const player1 = sortedParticipants[i];
    if (usedPlayers.has(player1.userId)) continue;

    let opponent = null;
    
    // 가능한 상대 찾기 (이전에 경기하지 않은 상대 우선)
    for (let j = i + 1; j < sortedParticipants.length; j++) {
      const player2 = sortedParticipants[j];
      if (!usedPlayers.has(player2.userId)) {
        const hasPlayedBefore = player1.previousOpponents?.includes(player2.userId) || false;
        if (!hasPlayedBefore) {
          opponent = player2;
          break;
        }
      }
    }

    // 모든 상대와 이미 경기한 경우, 가장 적게 경기한 상대 선택
    if (!opponent) {
      for (let j = i + 1; j < sortedParticipants.length; j++) {
        const player2 = sortedParticipants[j];
        if (!usedPlayers.has(player2.userId)) {
          opponent = player2;
          break;
        }
      }
    }

    if (opponent) {
      matches.push({
        id: generateMatchId('swiss', roundNumber, matchId, timestamp),
        roundNumber,
        matchNumber: matchId,
        player1,
        player2: opponent,
        winner: null,
        status: 'pending',
        scores: { player1: null, player2: null },
        createdAt: new Date().toISOString(),
        updatedAt: null
      });
      
      usedPlayers.add(player1.userId);
      usedPlayers.add(opponent.userId);
      matchId++;
    }
  }

  // 홀수 참가자 처리 (부전승)
  const leftoverPlayer = sortedParticipants.find(p => !usedPlayers.has(p.userId));
  if (leftoverPlayer) {
    matches.push({
      id: generateMatchId('swiss', roundNumber, 'bye', timestamp),
      roundNumber,
      matchNumber: matchId,
      player1: leftoverPlayer,
      player2: null,
      winner: leftoverPlayer.userId,
      status: 'bye',
      scores: { player1: 1, player2: 0 },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  }

  return {
    matches,
    totalMatches: matches.length,
    roundNumber,
    totalParticipants: participants.length,
    maxRounds: calculatedMaxRounds,
    type: 'swiss',
    createdAt: new Date().toISOString(),
    status: 'active'
  };
}

// 대진표 결과 업데이트 (트랜잭션 안전성 보장)
export function updateBracketResult(bracket, matchId, winnerId, scores) {
  if (!bracket || !matchId) {
    throw new Error('유효하지 않은 대진표 또는 매치 ID입니다.');
  }

  // 깊은 복사로 불변성 보장
  const updatedBracket = JSON.parse(JSON.stringify(bracket));
  updatedBracket.updatedAt = new Date().toISOString();

  switch (bracket.type) {
    case 'tournament':
      return updateTournamentResult(updatedBracket, matchId, winnerId, scores);
    case 'league':
      return updateLeagueResult(updatedBracket, matchId, winnerId, scores);
    case 'swiss':
      return updateSwissResult(updatedBracket, matchId, winnerId, scores);
    default:
      throw new Error('지원하지 않는 대진표 타입입니다.');
  }
}

// 토너먼트 결과 업데이트 (내부 함수)
function updateTournamentResult(bracket, matchId, winnerId, scores) {
  for (let roundIndex = 0; roundIndex < bracket.rounds.length; roundIndex++) {
    let round = bracket.rounds[roundIndex];
    // round가 배열이 아니면 Object.values로 변환
    if (!Array.isArray(round)) {
      round = Object.values(round);
    }
    const matchIndex = round.findIndex(match => match.id === matchId);
    
    if (matchIndex !== -1) {
      const match = round[matchIndex];
      
      // 매치 상태 업데이트
      match.winner = winnerId;
      match.status = 'completed';
      match.scores = scores || { player1: null, player2: null };
      match.updatedAt = new Date().toISOString();
      
      // 다음 라운드 진출자 설정
      if (roundIndex < bracket.rounds.length - 1) {
        const nextRound = bracket.rounds[roundIndex + 1];
        const nextMatchIndex = Math.floor(matchIndex / 2);
        const isFirstPlayer = matchIndex % 2 === 0;
        
        if (nextRound[nextMatchIndex]) {
          const winner = match.winner === match.player1?.userId ? match.player1 : match.player2;
          
          if (isFirstPlayer) {
            nextRound[nextMatchIndex].player1 = winner;
          } else {
            nextRound[nextMatchIndex].player2 = winner;
          }
          
          // 다음 매치 상태 업데이트
          const nextMatch = nextRound[nextMatchIndex];
          if (nextMatch.player1 && nextMatch.player2) {
            nextMatch.status = 'pending';
            nextMatch.updatedAt = new Date().toISOString();
          }
        }
      }
      break;
    }
  }
  return bracket;
}

// 리그전 결과 업데이트 (내부 함수)
function updateLeagueResult(bracket, matchId, winnerId, scores) {
  const matchIndex = bracket.matches.findIndex(match => match.id === matchId);
  
  if (matchIndex !== -1) {
    const match = bracket.matches[matchIndex];
    match.winner = winnerId;
    match.status = 'completed';
    match.scores = scores || { player1: null, player2: null };
    match.updatedAt = new Date().toISOString();
  } else {
    throw new Error('매치를 찾을 수 없습니다.');
  }
  
  return bracket;
}

// 스위스 시스템 결과 업데이트 (내부 함수)
function updateSwissResult(bracket, matchId, winnerId, scores) {
  const matchIndex = bracket.matches.findIndex(match => match.id === matchId);
  
  if (matchIndex !== -1) {
    const match = bracket.matches[matchIndex];
    match.winner = winnerId;
    match.status = 'completed';
    match.scores = scores || { player1: null, player2: null };
    match.updatedAt = new Date().toISOString();
    
    // 점수 업데이트 (스위스 시스템)
    if (match.player1 && match.player2) {
      // 이전 상대 기록 업데이트
      match.player1.previousOpponents = match.player1.previousOpponents || [];
      match.player2.previousOpponents = match.player2.previousOpponents || [];
      
      if (!match.player1.previousOpponents.includes(match.player2.userId)) {
        match.player1.previousOpponents.push(match.player2.userId);
      }
      if (!match.player2.previousOpponents.includes(match.player1.userId)) {
        match.player2.previousOpponents.push(match.player1.userId);
      }
      
      // 스위스 포인트 업데이트
      if (winnerId === match.player1.userId) {
        match.player1.swissPoints = (match.player1.swissPoints || 0) + 1;
        match.player2.swissPoints = (match.player2.swissPoints || 0);
      } else if (winnerId === match.player2.userId) {
        match.player2.swissPoints = (match.player2.swissPoints || 0) + 1;
        match.player1.swissPoints = (match.player1.swissPoints || 0);
      } else if (winnerId === 'draw') {
        match.player1.swissPoints = (match.player1.swissPoints || 0) + 0.5;
        match.player2.swissPoints = (match.player2.swissPoints || 0) + 0.5;
      }
    }
  } else {
    throw new Error('매치를 찾을 수 없습니다.');
  }
  
  return bracket;
}

// 1차원 rounds를 라운드별 2차원 배열로 그룹화
export function groupRoundsByRoundNumber(rounds) {
  if (!Array.isArray(rounds)) return [];
  const grouped = {};
  rounds.forEach(match => {
    if (!grouped[match.roundNumber]) grouped[match.roundNumber] = [];
    grouped[match.roundNumber].push(match);
  });
  return Object.values(grouped).sort((a, b) => a[0].roundNumber - b[0].roundNumber);
}

export function getTournamentProgress(bracket) {
  if (!bracket) return 0;
  
  let totalMatches = 0;
  let completedMatches = 0;
  
  if (bracket.rounds) {
    // 1차원 배열이면 라운드별로 그룹화
    const rounds = Array.isArray(bracket.rounds[0])
      ? bracket.rounds
      : groupRoundsByRoundNumber(bracket.rounds);
    rounds.forEach(round => {
      round.forEach(match => {
        if (match.status !== 'waiting') {
          totalMatches++;
          if (match.status === 'completed') completedMatches++;
        }
      });
    });
  } else if (bracket.matches) {
    // 리그전 또는 스위스 방식
    totalMatches = bracket.matches.length;
    completedMatches = bracket.matches.filter(m => m.status === 'completed').length;
  }
  
  return totalMatches > 0 ? Math.round((completedMatches / totalMatches) * 100) : 0;
}

// 리그전 순위표 생성
export function generateLeagueStandings(participants, matches) {
  if (!participants || !matches) return [];
  
  const standings = participants.map(participant => ({
    ...participant,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    goalDifference: 0,
    points: 0
  }));
  
  // 완료된 매치에서 통계 계산
  matches.filter(match => match.status === 'completed').forEach(match => {
    const player1Stats = standings.find(s => s.userId === match.player1?.userId);
    const player2Stats = standings.find(s => s.userId === match.player2?.userId);
    
    if (player1Stats && player2Stats) {
      player1Stats.played++;
      player2Stats.played++;
      
      const score1 = match.scores?.player1 || 0;
      const score2 = match.scores?.player2 || 0;
      
      player1Stats.goalsFor += score1;
      player1Stats.goalsAgainst += score2;
      player2Stats.goalsFor += score2;
      player2Stats.goalsAgainst += score1;
      
      if (match.winner === match.player1?.userId) {
        player1Stats.won++;
        player1Stats.points += 3;
        player2Stats.lost++;
      } else if (match.winner === match.player2?.userId) {
        player2Stats.won++;
        player2Stats.points += 3;
        player1Stats.lost++;
      } else if (match.winner === 'draw') {
        player1Stats.drawn++;
        player1Stats.points += 1;
        player2Stats.drawn++;
        player2Stats.points += 1;
      }
    }
  });
  
  // 득실차 계산 및 정렬
  standings.forEach(standing => {
    standing.goalDifference = standing.goalsFor - standing.goalsAgainst;
  });
  
  return standings.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
    return b.goalsFor - a.goalsFor;
  });
}

// 스위스 시스템 순위표 생성
export function generateSwissStandings(participants) {
  if (!participants) return [];
  
  return [...participants]
    .sort((a, b) => {
      const pointsA = a.swissPoints || 0;
      const pointsB = b.swissPoints || 0;
      if (pointsB !== pointsA) return pointsB - pointsA;
      return (b.tiebreaker || 0) - (a.tiebreaker || 0);
    })
    .map((participant, index) => ({
      ...participant,
      rank: index + 1,
      points: participant.swissPoints || 0
    }));
}

// 토너먼트 상태 확인 (배포 환경 시간대 처리)
export function getTournamentStatus(tournament, bracket = null) {
  if (!tournament) return 'unknown';
  
  const now = new Date();
  
  // 안전한 날짜 파싱
  const parseDate = (dateField) => {
    if (!dateField) return null;
    if (dateField.toDate && typeof dateField.toDate === 'function') {
      return dateField.toDate();
    }
    if (dateField instanceof Date) return dateField;
    if (typeof dateField === 'string') return new Date(dateField);
    return null;
  };
  
  const regStart = parseDate(tournament.registrationStart);
  const regEnd = parseDate(tournament.registrationEnd);
  const tournamentStart = parseDate(tournament.startDate);
  const tournamentEnd = parseDate(tournament.endDate);
  
  // 상태 로직 (시간대 안전성 고려)
  if (regStart && now < regStart) return 'upcoming';
  if (regStart && regEnd && now >= regStart && now <= regEnd) return 'registration';
  if (regEnd && tournamentStart && now > regEnd && now < tournamentStart) return 'waiting';
  
  if (tournamentStart && now >= tournamentStart) {
    if (bracket && isTournamentCompleted(bracket)) return 'completed';
    if (tournamentEnd && now > tournamentEnd) return 'completed';
    return 'active';
  }
  
  return 'active';
}

// 토너먼트 완료 여부 확인
export function isTournamentCompleted(bracket) {
  if (!bracket) return false;
  
  switch (bracket.type) {
    case 'tournament':
      const finalRound = bracket.rounds?.[bracket.rounds.length - 1];
      return finalRound && finalRound[0]?.status === 'completed';
      
    case 'league':
      return bracket.matches?.every(match => match.status === 'completed') || false;
      
    case 'swiss':
      const totalRounds = bracket.maxRounds || Math.ceil(Math.log2(bracket.totalParticipants));
      const currentRound = bracket.roundNumber || 1;
      return currentRound >= totalRounds &&
        bracket.matches?.filter(m => m.roundNumber === currentRound)
          .every(m => m.status === 'completed');
        
    default:
      return false;
  }
}

// 다음 라운드 생성 가능 여부 확인
export function canGenerateNextRound(bracket, currentRound) {
  if (!bracket || bracket.type !== 'swiss') return false;
  
  const currentRoundMatches = bracket.matches?.filter(m => m.roundNumber === currentRound) || [];
  const maxRounds = bracket.maxRounds || Math.ceil(Math.log2(bracket.totalParticipants));
  
  return currentRound < maxRounds && 
         currentRoundMatches.length > 0 && 
         currentRoundMatches.every(m => m.status === 'completed');
}

// 대회 통계 생성 (성능 최적화)
export function generateTournamentStats(tournament, bracket) {
  const stats = {
    totalParticipants: tournament.participants?.length || 0,
    totalMatches: 0,
    completedMatches: 0,
    progress: 0,
    averageScore: 0,
    topScorer: null,
    mostWins: null,
    lastUpdated: new Date().toISOString()
  };
  
  if (!bracket) return stats;
  
  // 매치 데이터 수집
  let allMatches = [];
  if (bracket.rounds) {
    allMatches = bracket.rounds.flat();
  } else if (bracket.matches) {
    allMatches = bracket.matches;
  }
  
  // 기본 통계
  stats.totalMatches = allMatches.filter(m => m.status !== 'waiting').length;
  stats.completedMatches = allMatches.filter(m => m.status === 'completed').length;
  stats.progress = stats.totalMatches > 0 ? 
    Math.round((stats.completedMatches / stats.totalMatches) * 100) : 0;
  
  // 점수 통계 (완료된 매치만)
  const completedMatches = allMatches.filter(m => 
    m.status === 'completed' && m.scores && 
    typeof m.scores.player1 === 'number' && typeof m.scores.player2 === 'number'
  );
  
  if (completedMatches.length > 0) {
    const totalScore = completedMatches.reduce((sum, match) => 
      sum + match.scores.player1 + match.scores.player2, 0
    );
    stats.averageScore = Math.round((totalScore / (completedMatches.length * 2)) * 100) / 100;
  }
  
  // 플레이어별 통계 (최적화된 계산)
  const playerStats = {};
  completedMatches.forEach(match => {
    if (match.player1) {
      const id = match.player1.userId;
      if (!playerStats[id]) playerStats[id] = { score: 0, wins: 0, name: match.player1.name };
      playerStats[id].score += match.scores.player1 || 0;
      if (match.winner === id) playerStats[id].wins++;
    }
    if (match.player2) {
      const id = match.player2.userId;
      if (!playerStats[id]) playerStats[id] = { score: 0, wins: 0, name: match.player2.name };
      playerStats[id].score += match.scores.player2 || 0;
      if (match.winner === id) playerStats[id].wins++;
    }
  });
  
  // 최고 득점자 및 최다 승자 찾기
  const players = Object.entries(playerStats);
  if (players.length > 0) {
    const topScorer = players.reduce((a, b) => a[1].score > b[1].score ? a : b);
    const mostWins = players.reduce((a, b) => a[1].wins > b[1].wins ? a : b);
    
    stats.topScorer = { id: topScorer[0], ...topScorer[1] };
    stats.mostWins = { id: mostWins[0], ...mostWins[1] };
  }
  
  return stats;
}

// 라운드 이름 생성 (다국어 지원 고려)
export function getRoundName(roundNumber, totalRounds, type = 'tournament') {
  if (type === 'swiss') return `${roundNumber}라운드`;
  if (type === 'league') return `${roundNumber}차전`;
  
  const remainingRounds = totalRounds - roundNumber + 1;
  const roundNames = {
    1: '결승',
    2: '준결승', 
    3: '준준결승',
    4: '8강',
    5: '16강',
    6: '32강',
    7: '64강'
  };
  
  return roundNames[remainingRounds] || `${roundNumber}라운드`;
}

// 매치 결과 유효성 검증 (강화된 검증)
export function validateMatchResult(match, winnerId, scores) {
  const errors = [];
  
  // 기본 검증
  if (!match) {
    errors.push('매치 정보가 없습니다.');
    return errors;
  }
  
  if (match.status === 'completed') {
    errors.push('이미 완료된 매치입니다.');
  }
  
  if (match.status === 'waiting') {
    errors.push('아직 시작되지 않은 매치입니다.');
  }
  
  // 승자 검증
  if (!winnerId && winnerId !== 'draw') {
    errors.push('승자를 선택해주세요.');
  }
  
  const validWinners = [match.player1?.userId, match.player2?.userId, 'draw', 'bye'].filter(Boolean);
  if (winnerId && !validWinners.includes(winnerId)) {
    errors.push('유효하지 않은 승자입니다.');
  }
  
  // 점수 검증
  if (scores) {
    if (typeof scores.player1 !== 'number' || typeof scores.player2 !== 'number') {
      errors.push('점수는 숫자여야 합니다.');
    }
    
    if (scores.player1 < 0 || scores.player2 < 0) {
      errors.push('점수는 0 이상이어야 합니다.');
    }
    
    if (winnerId !== 'draw' && scores.player1 === scores.player2) {
      errors.push('동점인 경우 무승부로 처리해주세요.');
    }
    
    if (winnerId === match.player1?.userId && scores.player1 <= scores.player2) {
      errors.push('승자의 점수가 더 높아야 합니다.');
    }
    
    if (winnerId === match.player2?.userId && scores.player2 <= scores.player1) {
      errors.push('승자의 점수가 더 높아야 합니다.');
    }
  }
  
  return errors;
}

// 대진표 내보내기 (텍스트 형태) - 배포 환경 최적화
export function exportBracketToText(bracket, tournament) {
  if (!bracket || !tournament) {
    return '대진표 정보가 없습니다.';
  }

  let text = `=== ${tournament.name || '대회'} 대진표 ===\n`;
  text += `생성일: ${new Date().toLocaleDateString()}\n`;
  text += `참가자 수: ${bracket.totalParticipants || 0}명\n\n`;
  
  try {
    if (bracket.type === 'tournament') {
      bracket.rounds?.forEach((round, roundIndex) => {
        const roundName = getRoundName(roundIndex + 1, bracket.totalRounds);
        text += `[${roundName}]\n`;
        
        round.forEach((match, matchIndex) => {
          const player1Name = match.player1?.name || '대기';
          const player2Name = match.player2?.name || '대기';
          let status = '';
          
          if (match.status === 'completed') {
            const winner = match.winner === match.player1?.userId ? player1Name : player2Name;
            const scores = match.scores ? 
              `${match.scores.player1 || 0}:${match.scores.player2 || 0}` : '';
            status = `(승자: ${winner} ${scores})`;
          } else {
            status = `(${match.status === 'pending' ? '예정' : '대기'})`;
          }
          
          text += `  경기 ${matchIndex + 1}: ${player1Name} vs ${player2Name} ${status}\n`;
        });
        text += '\n';
      });
    } else if (bracket.type === 'league') {
      text += '[리그전 경기 일정]\n';
      bracket.matches?.forEach((match, index) => {
        const player1Name = match.player1?.name || '미정';
        const player2Name = match.player2?.name || '미정';
        let status = '';
        
        if (match.status === 'completed') {
          status = `(${match.scores?.player1 || 0}:${match.scores?.player2 || 0})`;
        } else {
          status = `(${match.status === 'pending' ? '예정' : '대기'})`;
        }
        
        text += `  ${index + 1}. ${player1Name} vs ${player2Name} ${status}\n`;
      });
    } else if (bracket.type === 'swiss') {
      text += '[스위스 시스템 대진]\n';
      const rounds = {};
      
      bracket.matches?.forEach(match => {
        const roundNum = match.roundNumber || 1;
        if (!rounds[roundNum]) rounds[roundNum] = [];
        rounds[roundNum].push(match);
      });
      
      Object.keys(rounds).sort((a, b) => parseInt(a) - parseInt(b)).forEach(roundNum => {
        text += `\n${roundNum}라운드:\n`;
        rounds[roundNum].forEach((match, index) => {
          const player1Name = match.player1?.name || '미정';
          const player2Name = match.player2?.name || '미정';
          let status = '';
          
          if (match.status === 'completed') {
            status = match.status === 'bye' ? '(부전승)' : 
              `(${match.scores?.player1 || 0}:${match.scores?.player2 || 0})`;
          } else {
            status = `(${match.status === 'pending' ? '예정' : '대기'})`;
          }
          
          text += `  ${index + 1}. ${player1Name} vs ${player2Name} ${status}\n`;
        });
      });
    }
  } catch (error) {
    console.error('대진표 내보내기 오류:', error);
    text += '\n대진표 생성 중 오류가 발생했습니다.';
  }
  
  return text;
}

// 대진표 초기화 (안전성 강화)
export function resetBracket(bracket) {
  if (!bracket) {
    throw new Error('초기화할 대진표가 없습니다.');
  }
  
  try {
    const resetBracket = JSON.parse(JSON.stringify(bracket));
    resetBracket.updatedAt = new Date().toISOString();
    
    if (resetBracket.rounds) {
      // 토너먼트 방식 초기화
      resetBracket.rounds.forEach((round, roundIndex) => {
        round.forEach(match => {
          match.winner = null;
          match.updatedAt = null;
          match.scores = { player1: null, player2: null };
          
          // 첫 라운드가 아닌 경우 참가자도 초기화
          if (roundIndex > 0) {
            match.player1 = null;
            match.player2 = null;
            match.status = 'waiting';
          } else {
            // 첫 라운드는 상태만 초기화
            match.status = (match.player1 && match.player2) ? 'pending' :
              match.player1 || match.player2 ? 'bye' : 'waiting';
          }
        });
      });
    } else if (resetBracket.matches) {
      // 리그전/스위스 방식 초기화
      resetBracket.matches.forEach(match => {
        match.winner = null;
        match.status = match.status === 'bye' ? 'bye' : 'pending';
        match.scores = { player1: null, player2: null };
        match.updatedAt = null;
        
        // 스위스 시스템의 경우 포인트도 초기화
        if (resetBracket.type === 'swiss') {
          if (match.player1) {
            match.player1.swissPoints = 0;
            match.player1.previousOpponents = [];
          }
          if (match.player2) {
            match.player2.swissPoints = 0;
            match.player2.previousOpponents = [];
          }
        }
      });
      
      // 스위스 시스템 라운드 초기화
      if (resetBracket.type === 'swiss') {
        resetBracket.roundNumber = 1;
      }
    }
    
    return resetBracket;
  } catch (error) {
    console.error('대진표 초기화 오류:', error);
    throw new Error('대진표 초기화 중 오류가 발생했습니다.');
  }
}

// 매치 스케줄링 (배포 환경 시간대 처리)
export function scheduleMatches(matches, startDate, intervalMinutes = 30) {
  if (!matches || !Array.isArray(matches) || !startDate) {
    return matches;
  }
  
  try {
    const baseDate = new Date(startDate);
    if (isNaN(baseDate.getTime())) {
      throw new Error('유효하지 않은 시작 날짜입니다.');
    }
    
    return matches.map((match, index) => {
      const scheduledTime = new Date(baseDate.getTime() + (index * intervalMinutes * 60 * 1000));
      return {
        ...match,
        scheduledTime: scheduledTime.toISOString(),
        estimatedDuration: intervalMinutes
      };
    });
  } catch (error) {
    console.error('매치 스케줄링 오류:', error);
    return matches;
  }
}

// 대진표 검증 (데이터 무결성 확인)
export function validateBracket(bracket) {
  const errors = [];
  
  if (!bracket) {
    errors.push('대진표가 없습니다.');
    return errors;
  }
  
  if (!['tournament', 'league', 'swiss'].includes(bracket.type)) {
    errors.push('지원하지 않는 대진표 타입입니다.');
  }
  
  if (bracket.type === 'tournament') {
    if (!bracket.rounds || !Array.isArray(bracket.rounds)) {
      errors.push('토너먼트 라운드 정보가 없습니다.');
    } else {
      // 라운드별 매치 수 검증
      bracket.rounds.forEach((round, index) => {
        const expectedMatches = Math.pow(2, bracket.totalRounds - index - 1);
        if (round.length !== expectedMatches) {
          errors.push(`${index + 1}라운드 매치 수가 올바르지 않습니다.`);
        }
      });
    }
  } else if (['league', 'swiss'].includes(bracket.type)) {
    if (!bracket.matches || !Array.isArray(bracket.matches)) {
      errors.push('매치 정보가 없습니다.');
    }
  }
  
  return errors;
}

// 성능 최적화: 캐시된 통계 계산
const statsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5분

export function getCachedTournamentStats(tournamentId, tournament, bracket) {
  const cacheKey = `${tournamentId}-${bracket?.updatedAt || tournament?.updatedAt}`;
  const cached = statsCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.stats;
  }
  
  const stats = generateTournamentStats(tournament, bracket);
  statsCache.set(cacheKey, {
    stats,
    timestamp: Date.now()
  });
  
  // 캐시 크기 제한 (메모리 관리)
  if (statsCache.size > 100) {
    const oldestKey = statsCache.keys().next().value;
    statsCache.delete(oldestKey);
  }
  
  return stats;
}

// 전체 export (배포 환경에서 Tree-shaking 지원)
export default {
  // 대진표 생성
  generateTournamentBracket,
  generateLeagueBracket,
  generateSwissBracket,
  
  // 대진표 관리
  updateBracketResult,
  resetBracket,
  validateBracket,
  
  // 진행상황 및 통계
  getTournamentProgress,
  getTournamentStatus,
  isTournamentCompleted,
  canGenerateNextRound,
  generateTournamentStats,
  getCachedTournamentStats,
  
  // 순위 및 결과
  generateLeagueStandings,
  generateSwissStandings,
  
  // 유틸리티
  getRoundName,
  validateMatchResult,
  exportBracketToText,
  scheduleMatches,
  
  // 내부 유틸리티
  getNextPowerOfTwo,
  groupRoundsByRoundNumber
};